import get from 'lodash/get';
import set from 'lodash/set';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';

import {
  RESET_ANALYTICS_FILTER_SETTINGS_FOR_ROUTE,
  UPDATE_ANALYTICS_FILTER_SETTINGS_FOR_ROUTE,
  SET_IS_ROUTE_DETAILS_TAB_LOADING,

  FETCH_ROUTE_VISITS_CHART_DATA_FOR_ROUTE_SUCCESS,
  FETCH_ROUTE_VISITS_CHART_DATA_FOR_ROUTE_FAILURE,
  FETCH_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,
  FETCH_NEW_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST,
  FETCH_NEW_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_NEW_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,
  FETCH_VISIT_SESSION_DATA_FOR_ROUTE_REQUEST,
  FETCH_VISIT_SESSION_DATA_FOR_ROUTE_SUCCESS,
  FETCH_VISIT_SESSION_DATA_FOR_ROUTE_FAILURE,

  FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_REQUEST,
  FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_SUCCESS,
  FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_FAILURE,
  FETCH_SUBPAGE_DATA_FOR_ROUTE_REQUEST,
  FETCH_SUBPAGE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_SUBPAGE_DATA_FOR_ROUTE_FAILURE,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,

  FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_REQUEST,
  FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_SUCCESS,
  FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_FAILURE,
  FETCH_CONTENT_DATA_FOR_ROUTE_REQUEST,
  FETCH_CONTENT_DATA_FOR_ROUTE_SUCCESS,
  FETCH_CONTENT_DATA_FOR_ROUTE_FAILURE,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE,

  FETCH_REFERRAL_DATA_FOR_ROUTE_REQUEST,
  FETCH_REFERRAL_DATA_FOR_ROUTE_SUCCESS,
  FETCH_REFERRAL_DATA_FOR_ROUTE_FAILURE,
  FETCH_DEVICE_DATA_FOR_ROUTE_REQUEST,
  FETCH_DEVICE_DATA_FOR_ROUTE_SUCCESS,
  FETCH_DEVICE_DATA_FOR_ROUTE_FAILURE,

  MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_ROUTE,
  MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_ROUTE_SUBPAGE
} from '../const/action_types.js';

import { generateHash } from 'zoomforth_zfutils/hash_utils.js';
import { getDefaultFilterSettings } from 'zoomforth_zfutils/analytics_filters_utils';

import { get as getRoutes } from 'zoomforth_zf/appglobals/routes.js';

import {
  DEFAULT_ANALYTICS_FILTER_SETTINGS
} from '../const/analytics.js';

const DEFAULT_VISIT_DATA = { total_visits: 0, total_unique_visits: 0, data: [], after_key: null };

const _getDefaultFilterSettingsForRoute = (routeId)=> {
  if (isNil(routeId)) {
    return getDefaultFilterSettings(null, DEFAULT_ANALYTICS_FILTER_SETTINGS);
  }

  const route = getRoutes().get(routeId);

  if (!route) {
    throw new Error('Could not find a route with that ID');
  }

  return getDefaultFilterSettings(route.get('created_at'), DEFAULT_ANALYTICS_FILTER_SETTINGS);
};

// Create a cache key such that we fetch data only when the filters change
const _createCacheKey = (state, objectId)=> {
  const {
    startDate,
    endDate,
    shouldIgnoreZfUsers,
    shouldIgnoreAnonymousUsers,
    filteredEmailDomains,
    shorterThanDuration,
    longerThanDuration
  } = getAnalyticsFiltersForRouteId(state, objectId);

  // preprocess email domains so the hash can be deterministic while also not
  // caring about ordering of these domains.
  filteredEmailDomains.sort();

  return generateHash({
    objectId,
    // we want a predictable string for datetimes for a cache key
    startDate: startDate.format('MM/DD/YYYY'),
    endDate: endDate.format('MM/DD/YYYY'),
    shouldIgnoreZfUsers,
    shouldIgnoreAnonymousUsers,
    filteredEmailDomains,
    shorterThanDuration,
    longerThanDuration
  });
};

// unfortunately we cannot use `reselect` to memoize this selector since it
// takes an argument. we still get a massive benefit from memoizing this with lodash
const getMemoizedCacheKey = memoize((state, objectId)=> {
  return _createCacheKey(state, objectId);
});

export default (state = {}, action)=> {

  const cacheKey = (
    action.routeId ?
      getMemoizedCacheKey(state, action.routeId)
    :
      null
  );

  switch (action.type) {

  case RESET_ANALYTICS_FILTER_SETTINGS_FOR_ROUTE: {
    // defaulting on initial analytics load
    const analyticsFilterForThisRouteId = (
         state.analyticsFiltersByRouteId[action.routeId]
      || _getDefaultFilterSettingsForRoute(action.routeId)
    );

    return {
      ...state,
      analyticsFiltersByRouteId: {
        ...state.analyticsFiltersByRouteId,
        [action.routeId]: {
          ...analyticsFilterForThisRouteId,
          ...omit(_getDefaultFilterSettingsForRoute(action.routeId), ['startDate', 'endDate'])
        }
      }
    };
  }

  case UPDATE_ANALYTICS_FILTER_SETTINGS_FOR_ROUTE: {
    return {
      ...state,
      analyticsFiltersByRouteId: {
        ...state.analyticsFiltersByRouteId,
        [action.routeId]: {
          ...getAnalyticsFiltersForRouteId(state, action.routeId),
          ...action.filterSettings
        }
      }
    };
  }

  case SET_IS_ROUTE_DETAILS_TAB_LOADING: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        isRouteDetailsTabLoading: {
          ...state.analyticsData.isRouteDetailsTabLoading || {},
          [action.routeId]: action.isLoading
        }
      }
    };
  }

  case FETCH_ROUTE_VISITS_CHART_DATA_FOR_ROUTE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          routeVisitsChartData: isEmpty(action.data) ? DEFAULT_VISIT_DATA : action.data,
          routeVisitsChartDataError: null
        }
      }
    };
  }

  case FETCH_ROUTE_VISITS_CHART_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          routeVisitsChartData: DEFAULT_VISIT_DATA,
          routeVisitsChartDataError: action.error
        }
      }
    };
  }

  case FETCH_NEW_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          routeVisitsTableData: action.data,
          routeVisitsTableDataError: null
        }
      }
    };
  }

  case FETCH_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    var cachedVisitsData = cloneDeep(cachedRouteData.routeVisitsTableData) || DEFAULT_VISIT_DATA;
    var innerData = cachedVisitsData.data || [];

    if (!isEmpty(action.data)) {
      cachedVisitsData = {
        total_visits: action.data.total_visits,
        total_unique_visits: action.data.total_unique_visits,
        data: innerData.concat(action.data.data)
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          routeVisitsTableData: cachedVisitsData,
          routeVisitsTableDataError: null
        }
      }
    };
  }

  case FETCH_ROUTE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          routeVisitsTableData: DEFAULT_VISIT_DATA,
          routeVisitsTableDataError: action.error
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedRouteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          visitsData: {
            ...cachedVisitsData,
            [action.subpageId]: {
              ...cachedSessionData,
              visitSessionData: {},
              visitSessionDataError: null,
              isFetchingVisitSessionData: true
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedRouteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          visitsData: {
            ...cachedVisitsData,
            [action.pageSessionUuid]: {
              ...cachedSessionData,
              visitSessionData: {
                ...action.data
              },
              visitSessionDataError: null,
              isFetchingVisitSessionData: false
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedRouteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          visitsData: {
            ...cachedVisitsData,
            [action.pageSessionUuid]: {
              ...cachedVisitData,
              visitSessionData: {},
              visitSessionDataError: action.error,
              isFetchingVisitSessionData: false
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: true,
          topSubpagesData: [],
          topSubpagesDataError: null
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: false,
          topSubpagesData: action.data,
          topSubpagesDataError: null
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: false,
          topSubpagesData: [],
          topSubpagesDataError: action.error
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: true,
          topContentData: [],
          topContentDataError: null,
          contentData: {}
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: false,
          topContentData: action.data,
          topContentDataError: null,
          contentData: {}
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: false,
          topContentData: {},
          topContentDataError: action.error,
          contentData: {}
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: action.data,
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = get(cachedSubpagesData, `${action.subpageId}.subpageData`) || {};
    const cachedSubpageVisitEventsData = cachedSubpageData.visitEventsForSubpage || {};
    var visitEventsForSubpage = cachedSubpageVisitEventsData;
    var innerData = visitEventsForSubpage.data || [];

    if (!isEmpty(action.data)) {
      visitEventsForSubpage = {
        ...visitEventsForSubpage,
        ...action.data,
        data: innerData.concat(action.data.data)
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              subpageData: {
                ...cachedSubpageData,
                visitEventsForSubpage
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
              },
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
                visitEventsForSubpage: action.data
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedRouteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
              },
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_ROUTE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataLoading: true,
          referralData: [],
          referralDataError: null
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_ROUTE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataLoading: false,
          referralData: action.data,
          referralDataError: null
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataLoading: false,
          referralData: [],
          referralDataError: action.error
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_ROUTE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataLoading: true,
          deviceData: {},
          deviceDataError: null
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_ROUTE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataLoading: false,
          deviceData: action.data,
          deviceDataError: null
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_ROUTE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataLoading: false,
          deviceData: {},
          deviceDataError: action.error
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentData: {},
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentData: action.data,
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentData: {},
              contentDataError: action.error,
              isFetchingContentData: null
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};
    const innerContentData = cachedThisContentData.contentData || {};
    var visitsForContent = innerContentData.visitsForContent || {};
    var innerVisitsData = visitsForContent.data || [];

    if (!isEmpty(action.data)) {
      visitsForContent = {
        ...get(action, 'data.visitsForContent', {}),
        data: innerVisitsData.concat(get(action, 'data.visitsForContent.data', []))
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentData: {
                ...cachedThisContentData.contentData,
                visitsForContent
              },
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: action.error,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_REQUEST: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {},
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_SUCCESS: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {
                ...cachedThisContentData.contentData,
                ...action.data
              },
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_ROUTE_FAILURE: {
    const cachedRouteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedRouteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedRouteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {},
              contentDataError: action.error,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_ROUTE_SUBPAGE: {

    const { assetDataByAssetType } = action;

    const { analyticsData } = state;
    const cachedSiteData = analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};
    const cachedSubpageAllData = cachedSubpageData.subpageData || {};
    const cachedSubpageContentData = cachedSubpageAllData.topContentOnSubpage || {};

    const updatedContentData = map(cachedSubpageContentData, (content)=> {
      const { asset_id, asset_type } = content;

      if (!isNil(asset_type) && !isNil(asset_id)) {
        const assetData = get(assetDataByAssetType, `${asset_type}.assetData.${asset_id}`);

        return { ...content, asset: assetData };
      }

      return content;
    });

    return {
      ...state,
      analyticsData: {
        ...analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageAllData,
                topContentOnSubpage: {
                  ...cachedSubpageContentData,
                  ...updatedContentData
                }
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
        }
      }
    };
  }

  case MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_ROUTE: {
    const { assetDataByAssetType } = action;

    const { analyticsData } = state;
    const cachedSiteData = analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.topContentData || [];

    const updatedContentData = map(cachedContentData, (content)=> {
      const { asset_id, asset_type } = content;

      if (!isNil(asset_type) && !isNil(asset_id)) {
        const assetData = get(assetDataByAssetType, `${asset_type}.assetData.${asset_id}`);

        return { ...content, asset: assetData };
      }

      return content;
    });

    return {
      ...state,
      analyticsData: {
        ...analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          topContentData: updatedContentData,
          currentContentKey: action.contentUuid
        }
      }
    };
  }
    
  default:
    return state;
  }
};



export const resetCachedAnalyticsDataForRoute = (state, routeId) =>
  set(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}`, {});

/******************************************************************************
 * Filter Selectors
 *****************************************************************************/

export const getAnalyticsFiltersForRouteId = (state, routeId)=>
  get(state.analyticsFiltersByRouteId, routeId, _getDefaultFilterSettingsForRoute(routeId));

export const getIsRouteDetailsTabLoading = (state, routeId)=>
  get(state.analyticsData, `isRouteDetailsTabLoading.${routeId}`, false);

/******************************************************************************
 * Data Selectors
 *****************************************************************************/

// Route visits chart data

export const getRouteVisitsChartDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.routeVisitsChartData`, DEFAULT_VISIT_DATA);

export const getRouteVisitsChartDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.routeVisitsChartDataError`, null);

// Route visits table data

export const getRouteVisitsTableDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.routeVisitsTableData`, DEFAULT_VISIT_DATA);

export const getRouteVisitsTableDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.routeVisitsTableDataError`, null);

// Cached visit session data by pageSessionUuid

export const getIsVisitSessionDataForRouteLoading = (state, routeId, pageSessionUuid)=> {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.visitsData.${pageSessionUuid}.isFetchingVisitSessionData`, false);
};

export const getVisitSessionDataForRoute = (state, routeId, pageSessionUuid) => {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.visitsData.${pageSessionUuid}.visitSessionData`, {});
};

export const getVisitSessionDataForRouteError = (state, routeId, pageSessionUuid) => {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.visitsData.${pageSessionUuid}.visitSessionDataError`, null);
};

// Top supages data

export const getIsTopSubpagesDataForRouteLoading = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.isTopSubpagesDataLoading`, []);

export const getTopSubpagesDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.topSubpagesData`, []);

export const getTopSubpagesDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.topSubpagesDataError`, null);


export const getSubpagesDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData`, {});

export const getContentDataForRoute = (state, routeId) =>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.contentData`, {});

// Cached subpage data

export const getIsSubpageDataForRouteLoading = (state, routeId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData.${subpageId}.isFetchingSubpageData`, false);
};

export const getCachedSubpageDataForRoute = (state, routeId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData.${subpageId}.subpageData`, {});
};

export const getCachedSubpageDataForRouteError = (state, routeId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData.${subpageId}.subpageDataError`, null);
};

// Subpage visits table data

export const getSubpageVisitsTableDataForRoute = (state, routeId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData.${subpageId}.subpageData.visitEventsForSubpage`, DEFAULT_VISIT_DATA);
};

export const getSubpageVisitsTableDataForRouteError = (state, routeId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.subpagesData.${subpageId}.subpageData.visitEventsForSubpageError`, null);
};

// Cached content data by contentUuid

export const getIsContentDataForRouteLoading = (state, routeId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.contentData.${contentUuid}.isFetchingContentData`, false);
};

export const getCachedContentDataForRoute = (state, routeId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.contentData.${contentUuid}.contentData`, {});
};

export const getCachedContentDataForRouteError = (state, routeId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.contentData.${contentUuid}.contentDataError`, null);
};

// Top content data

export const getIsTopContentDataForRouteLoading = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.isTopContentDataLoading`, false);

export const getTopContentDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.topContentData`, {});

export const getTopContentDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.topContentDataError`, null);

// Referrals data

export const getIsReferralDataForRouteLoading = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.isReferralDataLoading`, []);

export const getReferralDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.referralData`, []);

export const getReferralDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.referralDataError`, null);

// Device data

export const getIsDeviceDataForRouteLoading = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.isDeviceDataLoading`, []);

export const getDeviceDataForRoute = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.deviceData`, {});

export const getDeviceDataForRouteError = (state, routeId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, routeId)}.deviceDataError`, null);
